import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { AudioPlayButton } from '@/components/audio/shared/AudioPlayButton';
import { getCoverForWorld } from '@/lib/covers';
import { useAudioStore } from '@/stores/audioStore';

/**
 * Mini-Audio-Player — Sönkes Figma `audio-player-mini-dark` Variante 02
 * (wrapper 4:1847). Visible over the Bottom-Nav on every app page EXCEPT
 * `/story/:id` (inline player lives there) and `/now-playing` (the full
 * player is the page). AppShell owns the visibility gate — this component
 * renders itself whenever it's mounted.
 *
 * Layout:
 *   - bg-gradient-to-r: from-dream-navy (Figma `gradient-end`, left)
 *                        to-world-aa   (Figma `gradient-start`, right)
 *   - height fixed at 68px across all breakpoints
 *   - desktop content caps at 768px via ContentContainer maxWidth="nav"
 */
export function MiniPlayer() {
  const navigate = useNavigate();

  const kind = useAudioStore((s) => s.kind);
  const storyId = useAudioStore((s) => s.storyId);
  const title = useAudioStore((s) => s.title);
  const childName = useAudioStore((s) => s.childName);
  const worldSlug = useAudioStore((s) => s.worldSlug);
  const url = useAudioStore((s) => s.url);
  const isPlaying = useAudioStore((s) => s.isPlaying);
  const hasPlayedOnce = useAudioStore((s) => s.hasPlayedOnce);
  const toggle = useAudioStore((s) => s.toggle);
  const close = useAudioStore((s) => s.close);

  // Gate: a single STORY loaded AND user has initiated playback at least once.
  // Matches briefing "wenn Audio läuft oder pausiert ist" — a story that
  // is merely pre-loaded (URL fetched on Story-Page mount for the iOS
  // gesture constraint) must not surface in the Mini-Player. The
  // series-combined track is deliberately gated off here (no storyId/childName
  // and controlled only from the season view) — `kind` makes that explicit.
  //
  // Catalog (Wissenswelten) is surfaced too: it has no storyId/hasPlayedOnce
  // (those are story-only — hasPlayedOnce is set only in play()'s story branch),
  // so its branch keys on a seated catalog track (kind==='catalog' && url),
  // which loadCatalog only sets when a Folge is loaded for playback. The story
  // predicate is left byte-identical.
  // Series-combined (the season Gesamt-Hörbuch) is surfaced too — same shape as
  // catalog: no storyId/hasPlayedOnce, keyed on a seated combined track
  // (loadCombined sets url + the season title). The combined player on the
  // season view is suppressed via AppShell's onSeasonPage, so the MiniPlayer
  // only appears once the user navigates away.
  const isStoryTrack = kind === 'story' && storyId && title && hasPlayedOnce;
  const isCatalogTrack = kind === 'catalog' && !!url && !!title;
  const isSeriesCombinedTrack = kind === 'series-combined' && !!url && !!title;
  if (!isStoryTrack && !isCatalogTrack && !isSeriesCombinedTrack) return null;

  return (
    <div
      className="fixed left-0 right-0 bottom-[calc(var(--pwa-bottom-nav-h,68px)+env(safe-area-inset-bottom,0))] z-40 h-[68px] bg-gradient-to-r from-dream-navy to-world-aa"
      role="region"
      aria-label="Audio-Player"
    >
      <ContentContainer maxWidth="nav">
        <div className="flex h-[68px] items-center justify-between gap-2 py-3">
          {/* Tap-area: cover + title stack. Click/Enter/Space expands to /now-playing. */}
          <button
            type="button"
            onClick={() => navigate('/now-playing')}
            aria-label="Zum Now-Playing wechseln"
            className="flex min-w-0 flex-1 items-center gap-2 text-left desktop:gap-3"
          >
            <img src={getCoverForWorld(worldSlug)} alt="" className="h-11 w-11 shrink-0 rounded" />
            <div className="flex min-w-0 flex-col gap-0.5 desktop:gap-1">
              <span
                className={cn(
                  'block truncate text-xs font-medium leading-none text-dream-white',
                  'desktop:text-sm',
                )}
              >
                {title}
              </span>
              {childName && (
                <span
                  className={cn(
                    'block truncate text-xxs leading-none text-dream-white opacity-75',
                    'desktop:text-xs',
                  )}
                >
                  für {childName}
                </span>
              )}
            </div>
          </button>

          {/* Controls */}
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={close}
              aria-label="Audio schließen"
              className="p-0.5 text-dream-white opacity-25 transition-opacity hover:opacity-60"
            >
              <X className="h-[18px] w-[18px]" strokeWidth={2.5} />
            </button>
            <AudioPlayButton
              size="mini"
              isPlaying={isPlaying}
              onToggle={() => {
                void toggle();
              }}
              ariaLabelPlay="Abspielen"
              ariaLabelPause="Pausieren"
            />
          </div>
        </div>
      </ContentContainer>
    </div>
  );
}
